import { useState } from 'react';

import { AddressPicker } from '../../../../../components/inputs/AddressPicker';
import { Checkbox } from '../../../../../components/inputs/Checkbox';
import { FormFieldset } from '../../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../../components/inputs/TextField';
import {
  ADDRESS_KIND,
  type PersonnelAddressInput,
  type PersonnelDetail,
} from '../../personnelTypes';
import { NO_CATALOGS, samplePersonnel } from '../../utils/samplePersonnel';

import { PersonnelSectionModal } from './PersonnelSectionModal';
import { useSectionForm } from './sectionForm';

const BLANK: Omit<PersonnelAddressInput, 'kind'> = {
  streetAddress: null,
  barangay: null,
  cityMunicipality: null,
  province: null,
  zipCode: null,
};

type Parts = Omit<PersonnelAddressInput, 'kind'>;

/**
 * Where this person lives — the Address card's editor.
 *
 * **This is the only way an address gets entered.** The create wizard has no address step, so
 * the panel had no input form behind it at all, which is why every record shows five em
 * dashes. That is also the answer to "is this section read-only": a card that has never been
 * fillable is not read-only, it is unfinished.
 *
 * The parts are plain text because that is what the record stores. A personnel address is what
 * someone **stated** at a point in time, so renaming a city in the `AddressPlaces` catalog must
 * not rewrite the addresses already collected — the catalog feeds a picker, and the picker
 * writes the name. The picker itself does not exist on the client yet; when it arrives it
 * replaces these inputs and the stored shape does not change.
 */
export function EditAddressModal({
  open,
  record,
  onClose,
  onSaved,
}: {
  open: boolean;
  record: PersonnelDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [present, setPresent] = useState<Parts>(BLANK);
  const [permanent, setPermanent] = useState<Parts>(BLANK);
  /*
    Checked by default, matching the create form. Most people's permanent address IS their
    present one, so the form opens on the common answer and the reader unticks to say
    otherwise rather than ticking to say the usual thing.
  */
  const [sameAsPresent, setSameAsPresent] = useState(true);

  function seed() {
    const stored = (kind: number) => record.addresses.find((a) => a.kind === kind) ?? null;
    const partsOf = (row: ReturnType<typeof stored>): Parts =>
      row
        ? {
            streetAddress: row.streetAddress,
            barangay: row.barangay,
            cityMunicipality: row.cityMunicipality,
            province: row.province,
            zipCode: row.zipCode,
          }
        : BLANK;

    const permanentRow = stored(ADDRESS_KIND.permanent);

    setPresent(partsOf(stored(ADDRESS_KIND.present)));
    setPermanent(partsOf(permanentRow));

    /*
      Checked unless the record actually carries a SEPARATE permanent address.

      The stored flag alone is not enough: it is false on every record nobody has asked yet,
      which is exactly the record this form is usually opened on — and that unticked box then
      puts a second empty address block in front of a reader who has not been asked for one.
      A missing permanent row means "never collected", not "deliberately different", so the
      form opens on the common answer and the reader unticks to say otherwise.

      A record that DOES hold a permanent address keeps its box unticked, because there the
      absence of the flag is a real statement rather than a default nobody set.
    */
    setSameAsPresent(record.permanentAddressSameAsPresent || permanentRow === null);
  }

  /*
    Autogenerate on an EDIT form — allowed since 14 Sep 2026. The catalogs are empty here
    because this section holds none; `samplePersonnel` only needs them for the pickers other
    sections own.
  */
  function generate() {
    const sample = samplePersonnel({ catalogs: NO_CATALOGS, identificationTypes: [] });

    setPresent(sample.presentAddress);

    /*
      The permanent block is filled BEHIND the tick, which stays where the record left it.
      A generator that flips it is one whose output the reader cannot predict — and on an
      edit form it would also overrule what the record actually says, which is worse than
      unpredictable: it looks like the stored answer.
    */
    setPermanent(sample.permanentAddress);
  }

  function build() {
    const rows: PersonnelAddressInput[] = [{ kind: ADDRESS_KIND.present, ...present }];

    /*
      A record whose permanent address IS the present one carries no permanent row — the
      server refuses a request that asserts both, because the two statements contradict. The
      flag is the statement; a missing row on its own would be ambiguous between "the same"
      and "never collected".
    */
    if (!sameAsPresent) {
      rows.push({ kind: ADDRESS_KIND.permanent, ...permanent });
    }

    return { addresses: rows, permanentAddressSameAsPresent: sameAsPresent };
  }

  return (
    <PersonnelSectionModal
      open={open}
      record={record}
      title="Edit address"
      idPrefix="personnel-address"
      onClose={onClose}
      onSaved={onSaved}
      onOpen={seed}
      onGenerate={generate}
      build={build}
    >
      <AddressFields
        legend="Present address"
        idPrefix="personnel-address-present"
        value={present}
        onChange={setPresent}
      />

      {/* The label wraps the box, matching the create form — `<Checkbox>` draws the control
          and nothing else, so the text and the `htmlFor` belong to the caller. */}
      <div className="border-t border-slate-200 pt-4">
        <label
          htmlFor="personnel-address-same"
          className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
        >
          <Checkbox
            id="personnel-address-same"
            name="personnel-address-same"
            checked={sameAsPresent}
            onChange={(e) => setSameAsPresent(e.target.checked)}
          />
          <span>The permanent address is the same as the present address</span>
        </label>
      </div>

      {/*
        Hidden rather than disabled: with the box ticked there is no permanent address to
        collect, and a greyed-out copy of five fields is a form asking a question it has just
        been told does not apply.
      */}
      {sameAsPresent ? null : (
        <AddressFields
          legend="Permanent address"
          idPrefix="personnel-address-permanent"
          value={permanent}
          onChange={setPermanent}
        />
      )}
    </PersonnelSectionModal>
  );
}

/** One address, five parts. Both addresses are the same shape, so they are the same component. */
function AddressFields({
  legend,
  idPrefix,
  value,
  onChange,
}: {
  legend: string;
  idPrefix: string;
  value: Parts;
  onChange: (next: Parts) => void;
}) {
  const { saving, error } = useSectionForm();
  const set = (patch: Partial<Parts>) => onChange({ ...value, ...patch });

  // Empty is null on the wire, never "": the column is nullable and a blank string would read
  // as an address part that was answered with nothing.
  const trimmed = (raw: string) => (raw.trim() === '' ? null : raw);

  return (
    /*
      FULL WIDTH, stacked — never a two-column grid. This block used to be five text inputs
      in `sm:grid-cols-2`, which gave the address the same room as a blood type: the picker
      shows a whole path (`Rizal, Antipolo, Bagong Nayon`) and a street line carries a lot
      number, a block, a street and a subdivision. Both are long values, and a half-width box
      truncates the one thing the reader is checking.
    */
    <FormFieldset legend={legend}>
      <AddressPicker
        id={`${idPrefix}-place`}
        value={{
          province: value.province,
          cityMunicipality: value.cityMunicipality,
          barangay: value.barangay,
        }}
        onChange={(next) => set(next)}
        error={
          error?.fieldError('province') ??
          error?.fieldError('cityMunicipality') ??
          error?.fieldError('barangay')
        }
        disabled={saving}
      />

      {/*
        The street line and the postal code share a row, split 3:1.

        NOT equal halves. The street is one line for everything below the barangay — lot,
        block, street, building, subdivision, unit — and people write the address they have in
        the order their own address uses, so it needs the room. A postal code is four digits,
        and a box sized like the street beside it says it is something longer.
      */}
      <div className="grid gap-4 sm:grid-cols-[3fr_1fr]">
        <TextField
          id={`${idPrefix}-street`}
          label="Lot No / Street / Bldg"
          placeholder="e.g. Lot 12, Block 3, Mango St., Greenview Subd."
          value={value.streetAddress ?? ''}
          onChange={(e) => set({ streetAddress: trimmed(e.target.value) })}
          error={error?.fieldError('streetAddress')}
          disabled={saving}
        />

        <TextField
          id={`${idPrefix}-zip`}
          label="Postal code"
          value={value.zipCode ?? ''}
          onChange={(e) => set({ zipCode: trimmed(e.target.value) })}
          error={error?.fieldError('zipCode')}
          disabled={saving}
        />
      </div>
    </FormFieldset>
  );
}
